import React, {useCallback, useMemo} from 'react'
import {getColorFromPlayerColor} from './UIGamePlayerInfoContainerUtils'
import {useGameStore} from '../../store/GameStoreContext'
import {useIsPlayerTakingAction} from './useIsPlayerTakingAction'
import {usePlayerVictoryPoints} from './usePlayerVictoryPoints'
// eslint-disable-next-line no-restricted-imports
import {getLobbyImagePath} from '../../../../../code/client/shared/scripts/ui_utils/Utils'
import {usePlayerStatus} from './usePlayerStatus'
import {useGameTimer} from '../../hooks/useGameTimer'
import {usePlayerInfoSectionContentHandProps} from './sections/usePlayerInfoSectionContentHandProps'
import {usePlayerInfoSectionContentUsedDevsProps} from './sections/usePlayerInfoSectionContentUsedDevsProps'
import {usePlayerInfoSectionContentCKProps} from './sections/usePlayerInfoSectionContentCKProps'
import {usePlayerInfoSectionContentPiecesProps} from './sections/usePlayerInfoSectionContentPiecesProps'
import {
    UIGamePlayerInfoFooterProps,
    UIGamePlayerInfoSectionContentHand,
    UIGamePlayerInfoSection,
    UIGamePlayerInfoHeaderPropsOmitted,
    UIGamePlayerInfo,
    UIGamePlayerInfoSectionContentUsedDevs,
    UIGamePlayerInfoSectionContentCK,
    UIGamePlayerInfoSectionContentPieces,
} from '../UIGamePlayerInfo'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator'
import {useGameValidator} from '../../store/selectors/useGameValidator'
import {
    UIGamePlayerInfoExtraFooterProps,
    UIGamePlayerInfoExtraFooterDice,
} from '../UIGamePlayerInfo/UIGamePlayerInfoExtraFooter/UIGamePlayerInfoExtraFooter'
import {PlayerColorWithoutNone} from '../../utils/gameColors'
import {LobbyImage, ModeSettingEnum} from '@colonist/types'
import {TurnState, ActionState} from '@colonist/game-states'
import {usePlayerValidators} from '../../store/selectors/usePlayerValidators'
import {UIGamePlayerOptionsDropdown} from '../../common/UIGamePlayerOptionsDropdown/UIGamePlayerOptionsDropdown'
import {CurrentStateMessageUtils} from '../UIGameCurrentStatus/CurrentStateMessageUtils'

export type PlayerInfoSection = 'hand' | 'usedDevs' | 'ck' | 'pieces'

export interface UIGamePlayerInfoContainerProps {
    playerColor: PlayerColorWithoutNone
    visibleSections: PlayerInfoSection[]
}

export const UIGamePlayerInfoContainer = ({
    playerColor,
    visibleSections,
}: UIGamePlayerInfoContainerProps) => {
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const myPlayerColor = useGameStore((state) => state.myColor)
    const isMyPlayer = playerColor === myPlayerColor
    const color = getColorFromPlayerColor(playerColor)
    const userState = useGameStore((state) => state.gameUserStates.getUserState(playerColor))
    const isConnected = useGameStore((state) => state.gameState.playerStates[playerColor]?.isConnected ?? false)
    const isActiveHumanSession = !userState.isBot && isConnected

    const isPlayerTakingAction = useIsPlayerTakingAction(playerColor)
    const {publicVictoryPoints, privateVictoryPoints} = usePlayerVictoryPoints(playerColor)

    const currentStateValidator = useCurrentStateValidator()
    const gameValidator = useGameValidator()
    const isCurrentTurnPlayer =
        currentStateValidator.currentTurnPlayers.includes(playerColor)
        && (currentStateValidator.currentTurnPlayers.length === 1 || playerColor === myPlayerColor)
    const isTakingOutOfTurnAction = isPlayerTakingAction && !isCurrentTurnPlayer
    const isMechanicTurnColonistRushEnabled =
        useGameStore((state) => state.gameState.experimentalMechanicState?.mechanicTurnColonistRushState !== undefined)
    const isToggledEndTurn = isMechanicTurnColonistRushEnabled && currentStateValidator.hasPlayerToggledEndTurn(playerColor)
    const headerProps = useMemo<UIGamePlayerInfoHeaderPropsOmitted>(() => ({
        avatar: (isActiveHumanSession == true)
            ? (userState.profilePictureUrl ?? getLobbyImagePath(userState.databaseIcon))
            : getLobbyImagePath(LobbyImage.Bot),
        username: userState.username,
        isToggledEndTurn: isToggledEndTurn,
        pointsPublic: publicVictoryPoints,
        pointsPrivate: privateVictoryPoints - publicVictoryPoints,
    }), [userState, isToggledEndTurn, publicVictoryPoints, privateVictoryPoints, isActiveHumanSession])

    const playerStatus = usePlayerStatus(playerColor)
    const timeLeftInSeconds = useGameStore((state) => state.timeLeftInSeconds)
    const isPaused = useGameStore((state) => state.gamePause.isPaused)
    const liveTimeLeftInSeconds = useGameTimer({
        timeLeftInSeconds: timeLeftInSeconds,
        isPaused: isPaused,
    })
    const turnState = useGameStore((state) => state.gameState.currentState.turnState)
    const actionState = useGameStore((state) => state.gameState.currentState.actionState)
    const playerActionState = currentStateValidator.getPlayerActionState(playerColor)
    const playersTakingAction = gameValidator.getPlayersTakingAction()
    const isWaitingForOtherPlayers = actionState !== ActionState.None && !playersTakingAction.includes(playerColor)
    const isMyPlayerTakingAction = playersTakingAction.includes(myPlayerColor)
    const footerValueProps = useMemo<UIGamePlayerInfoFooterProps>(() => ({
        text: playerStatus,
        timeLeftInSeconds: liveTimeLeftInSeconds,
        isLowTimeEnabled: !isMechanicTurnColonistRushEnabled && (
            isMyPlayer
                ? !isWaitingForOtherPlayers
                : isCurrentTurnPlayer && isMyPlayerTakingAction
        ),
        hideTimer: modeSetting === ModeSettingEnum.BeginnerModeClassic4P,
        hideText: (isMechanicTurnColonistRushEnabled && playerActionState === ActionState.None)
            || isWaitingForOtherPlayers,
    }), [playerStatus, liveTimeLeftInSeconds, isMyPlayer, isMechanicTurnColonistRushEnabled,
        modeSetting, playerActionState, isWaitingForOtherPlayers, isCurrentTurnPlayer, isMyPlayerTakingAction])
    const shouldRenderFooter = isCurrentTurnPlayer && turnState !== TurnState.SpecialBuildPhase
    const footerProps = shouldRenderFooter ? footerValueProps : undefined

    const isRequestingSpecialBuildPhase = useGameStore((state) =>
        (state.gameState.mechanicSpecialBuildPhaseState?.playerStates?.[playerColor]?.isRequesting) == true || false)
    const hasMerchant = useGameStore((state) =>
        state.gameState.mechanicMerchantState?.owner === playerColor)
    const hasRobber = currentStateValidator.nextPlayerToMoveRobber === playerColor

    const diceState = useGameStore((state) => state.gameState.diceState)
    const eventDiceState = useGameStore((state) => state.gameState.mechanicEventDiceState)
    const lastPlayerToRollDice = useGameStore((state) =>
        state.gameState.mechanicSpecialBuildPhaseState?.lastPlayerToRollDice)
    const isPlayerForDice = turnState === TurnState.SpecialBuildPhase
        ? lastPlayerToRollDice === playerColor
        : isCurrentTurnPlayer
    const showDice = isPlayerForDice && !isMyPlayer && turnState !== TurnState.InitialPlacement
    const dice = useMemo<UIGamePlayerInfoExtraFooterDice | undefined>(() => {
        if(!showDice) return undefined
        const result: UIGamePlayerInfoExtraFooterDice = {
            dice1: diceState.dice1,
            dice2: diceState.dice2,
        }
        if(eventDiceState !== undefined) {
            result.eventDice = eventDiceState.eventDice
        }
        return result
    }, [showDice, diceState.dice1, diceState.dice2, eventDiceState])

    const isInSpecialBuildPhase = isCurrentTurnPlayer && turnState === TurnState.SpecialBuildPhase
    const extraFooterProps = useMemo<UIGamePlayerInfoExtraFooterProps>(() => {
        const props: UIGamePlayerInfoExtraFooterProps = {
            isSpecialBuildPhaseRequested: isRequestingSpecialBuildPhase,
            isMerchant: hasMerchant,
            isRushNextRobber: hasRobber,
            isWaiting: isTakingOutOfTurnAction,
            playerColor: playerColor,
            playerUsername: userState.username,
        }
        if(dice !== undefined) {
            props.dice = dice
        }
        if(isTakingOutOfTurnAction || isInSpecialBuildPhase) {
            const outOfTurnActionText = CurrentStateMessageUtils.getPlayerInfoExtraFooterActionStateText({
                turnState: turnState,
                actionState: playerActionState,
            })
            if(outOfTurnActionText !== undefined) props.outOfTurnActionText = outOfTurnActionText
            if(turnState === TurnState.SpecialBuildPhase) props.timeLeftInSeconds = liveTimeLeftInSeconds
        }
        return props
    }, [isRequestingSpecialBuildPhase, hasMerchant, hasRobber, isTakingOutOfTurnAction, playerColor, dice,
        isInSpecialBuildPhase, playerActionState, liveTimeLeftInSeconds, turnState])

    const {getPlayerValidator} = usePlayerValidators()
    const playerValidator = getPlayerValidator(playerColor)
    const headerWrapper = useCallback((headerElement: React.ReactNode) => (
        <UIGamePlayerOptionsDropdown playerValidator={playerValidator}>
            {headerElement}
        </UIGamePlayerOptionsDropdown>
    ), [playerValidator])

    const sectionContentHandProps = usePlayerInfoSectionContentHandProps(playerColor)
    const sectionContentUsedDevsProps = usePlayerInfoSectionContentUsedDevsProps(playerColor)
    const sectionContentCKProps = usePlayerInfoSectionContentCKProps(playerColor)
    const sectionContentPiecesProps = usePlayerInfoSectionContentPiecesProps(playerColor)

    return (
        <UIGamePlayerInfo
            color={color}
            isMyPlayer={isMyPlayer}
            header={headerProps}
            headerWrapper={headerWrapper}
            footer={footerProps}
            extraFooter={extraFooterProps}>
            {visibleSections.includes('hand') && <UIGamePlayerInfoSection>
                <UIGamePlayerInfoSectionContentHand {...sectionContentHandProps} />
            </UIGamePlayerInfoSection>}
            {visibleSections.includes('pieces') && <UIGamePlayerInfoSection>
                <UIGamePlayerInfoSectionContentPieces {...sectionContentPiecesProps} />
            </UIGamePlayerInfoSection>}
            {visibleSections.includes('usedDevs') && sectionContentUsedDevsProps && <UIGamePlayerInfoSection>
                <UIGamePlayerInfoSectionContentUsedDevs {...sectionContentUsedDevsProps} />
            </UIGamePlayerInfoSection>}
            {visibleSections.includes('ck') && sectionContentCKProps && <UIGamePlayerInfoSection>
                <UIGamePlayerInfoSectionContentCK {...sectionContentCKProps} />
            </UIGamePlayerInfoSection>}
        </UIGamePlayerInfo>
    )
}
